import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/hooks/use-auth-wrapper'
import { usePermissions } from '@/hooks/use-permissions'
import { useChurch } from '@/hooks/useChurch'
import { useChurchColors } from '@/hooks/useChurchColors'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { getInitials } from '@/lib/utils'
import {
  LayoutDashboard,
  Calendar,
  Book,
  Users,
  UserCircle,
  User,
  LogOut,
  ScrollText,
  DollarSign,
  Shield,
  Building,
  HandHeart,
  ArrowLeft,
  Sparkles,
  Settings,
  Moon,
  Sun,
} from 'lucide-react'
import { useThemeStore } from '@/stores/theme.store'

// Definindo tipos para a estrutura de navegação
interface NavigationItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string
  description?: string
}

const navigation: NavigationItem[] = [
  { 
    name: 'Principal', 
    href: '/', 
    icon: LayoutDashboard,
    description: 'Painel principal da igreja'
  },
  { 
    name: 'Contribuição', 
    href: '/contribuicao', 
    icon: HandHeart,
    description: 'Faça sua contribuição'
  },
  { 
    name: 'Eventos', 
    href: '/eventos', 
    icon: Calendar, 
    badge: '3',
    description: 'Eventos e atividades'
  },
  { 
    name: 'Bíblia', 
    href: '/biblia', 
    icon: Book,
    description: 'Leitura bíblica'
  },
  { 
    name: 'Ministérios', 
    href: '/ministerios', 
    icon: Users,
    description: 'Ministérios da igreja'
  },
  { 
    name: 'Membros', 
    href: '/membros', 
    icon: UserCircle,
    description: 'Membros da igreja'
  },
  { 
    name: 'Sermões', 
    href: '/sermoes', 
    icon: ScrollText,
    description: 'Sermões e palavras'
  },
  { 
    name: 'Finanças', 
    href: '/financas', 
    icon: DollarSign,
    description: 'Gestão financeira'
  },
]

export function MenuMobilePage() {
  const navigate = useNavigate()
  const { perfil, logout } = useAuth()
  const { canManageChurch } = usePermissions()
  const { theme, setTheme } = useThemeStore()
  const { church, currentLogo, hasLogo } = useChurch()
  const { primaryColor, secondaryColor, titleColor, descriptionColor } = useChurchColors()

  const avatarUrl = (perfil as { avatar_url?: string } | null)?.avatar_url

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light')
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  // Criar array de navegação dinâmico baseado nas permissões do usuário
  const dynamicNavigation = React.useMemo(() => {
    const nav = [...navigation]
    
    // Adicionar "Minha Igreja" se o usuário for admin ou gestor
    if (canManageChurch()) {
      nav.push({ 
        name: 'Minha Igreja', 
        href: '/minha-igreja', 
        icon: Building,
        description: 'Configurações da igreja'
      })
    }
    
    // Adicionar Admin App se o usuário for admin
    if (perfil?.is_admin) {
      nav.push({ 
        name: 'Admin App', 
        href: '/admin-app', 
        icon: Shield,
        description: 'Painel administrativo'
      })
    }
    
    return nav
  }, [perfil?.is_admin, canManageChurch])

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/10 pb-safe">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b bg-card/95 backdrop-blur-lg supports-[backdrop-filter]:bg-card/80">
        <div className="flex items-center justify-between p-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          
          <h1 className="text-lg font-semibold">Menu</h1>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className="gap-2"
          >
            {theme === 'light' ? (
              <Moon className="h-4 w-4" />
            ) : (
              <Sun className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Church Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-card to-muted/20 p-6"
        >
          <div className="flex items-center gap-4">
            {hasLogo && currentLogo ? (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="relative h-16 w-16 overflow-hidden rounded-2xl border-2 border-white/20 bg-white/10 backdrop-blur-sm"
              >
                <img
                  src={currentLogo}
                  alt={church?.nome || 'Logo da Igreja'}
                  className="h-full w-full object-cover"
                />
              </motion.div>
            ) : (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-white/20 bg-white/10 backdrop-blur-sm"
              >
                <Sparkles className="h-8 w-8 text-white" />
              </motion.div>
            )}
            <div className="flex-1 min-w-0">
              <h2 
                className="text-xl font-bold truncate"
                style={{ color: titleColor }}
                title={church?.nome || 'IgrejaDigital'}
              >
                {church?.nome || 'IgrejaDigital'}
              </h2>
              {church?.descricao && (
                <p 
                  className="text-sm mt-1 opacity-75 truncate"
                  style={{ color: descriptionColor }}
                  title={church.descricao}
                >
                  {church.descricao}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* User Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Avatar 
                    className="h-12 w-12 border-2"
                    style={{ borderColor: `${primaryColor}33` }}
                  >
                    <AvatarImage src={avatarUrl} />
                    <AvatarFallback 
                      className="text-sm text-white font-semibold"
                      style={{
                        backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`
                      }}
                    >
                      {getInitials(perfil?.nome || 'U')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-background bg-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{perfil?.nome}</p>
                  <Badge 
                    variant="secondary" 
                    className="text-xs capitalize"
                    style={{
                      backgroundColor: `${secondaryColor}20`,
                      color: secondaryColor,
                      borderColor: `${secondaryColor}40`
                    }}
                  >
                    {perfil?.permissao}
                  </Badge>
                  {perfil?.igreja?.nome && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {perfil.igreja.nome}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/perfil')}
                  className="flex-1 gap-2"
                  style={{
                    borderColor: `${primaryColor}40`,
                    color: primaryColor
                  }}
                >
                  <User className="h-4 w-4" />
                  Perfil
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/configuracoes')}
                  className="flex-1 gap-2"
                >
                  <Settings className="h-4 w-4" />
                  Config
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="flex-1 gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                >
                  <LogOut className="h-4 w-4" />
                  Sair
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Navigation Cards */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Navegação
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {dynamicNavigation.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
              >
                <Link to={item.href}>
                  <Card className="group transition-all hover:shadow-md active:scale-95">
                    <CardContent className="p-4">
                      <div className="flex flex-col items-center text-center space-y-3">
                        <div 
                          className="flex h-12 w-12 items-center justify-center rounded-xl transition-all group-hover:scale-110"
                          style={{
                            backgroundImage: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}10)`,
                            color: primaryColor
                          }}
                        >
                          <item.icon className="h-6 w-6" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center justify-center gap-2">
                            <p className="font-semibold text-sm">{item.name}</p>
                            {item.badge && (
                              <Badge
                                variant="secondary"
                                className="h-5 min-w-5 px-1.5 text-xs"
                                style={{
                                  backgroundColor: `${secondaryColor}20`,
                                  color: secondaryColor,
                                  borderColor: `${secondaryColor}40`
                                }}
                              >
                                {item.badge}
                              </Badge>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Acesso Rápido
          </h3>
          <div className="grid grid-cols-1 gap-3">
            <Link to="/eventos/novo">
              <Card className="group transition-all hover:shadow-md active:scale-95">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="flex h-10 w-10 items-center justify-center rounded-lg"
                      style={{
                        backgroundImage: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}b3)`
                      }}
                    >
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">Criar Evento</p>
                      <p className="text-xs text-muted-foreground">Novo evento da igreja</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/membros/novo">
              <Card className="group transition-all hover:shadow-md active:scale-95">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="flex h-10 w-10 items-center justify-center rounded-lg"
                      style={{
                        backgroundImage: `linear-gradient(135deg, ${secondaryColor}, ${secondaryColor}b3)`
                      }}
                    >
                      <UserCircle className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">Adicionar Membro</p>
                      <p className="text-xs text-muted-foreground">Cadastrar novo membro</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}